(() => {
  const CART_UPDATE_EVENT = 'cart:update';
  const DISCOUNT_UPDATE_EVENT = 'discount:update';
  const MAX_SECTIONS = 5;

  class CartItemsComponent extends HTMLElement {
    #isUpdating = false;
    #needsRefresh = false;

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      this.addEventListener('change', this.#onChange);
      this.addEventListener('keydown', this.#onKeyDown);
      document.addEventListener(CART_UPDATE_EVENT, this.#onExternalCartUpdate);
      document.addEventListener(DISCOUNT_UPDATE_EVENT, this.#onExternalCartUpdate);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('change', this.#onChange);
      this.removeEventListener('keydown', this.#onKeyDown);
      document.removeEventListener(CART_UPDATE_EVENT, this.#onExternalCartUpdate);
      document.removeEventListener(DISCOUNT_UPDATE_EVENT, this.#onExternalCartUpdate);
    }

    get sectionId() {
      const id = this.dataset.sectionId;
      if (!id) throw new Error('Cart section id missing');
      return id;
    }

    /**
     * Re-render this cart component from Shopify's current cart.
     * This is used when a persistent header drawer is opened after the cart
     * changed on another page or in another cart component.
     */
    async refresh() {
      if (this.#isUpdating) {
        this.#needsRefresh = true;
        return;
      }

      try {
        await this.#refreshSection();
      } catch (error) {
        console.error('[Zyma cart] manual refresh failed', error);
      }
    }

    #onClick = (event) => {
      if (!(event.target instanceof Element) || this.#isUpdating) return;

      const action = event.target.closest('[data-zyma-cart-increase], [data-zyma-cart-decrease], [data-zyma-cart-remove]');
      if (!(action instanceof HTMLButtonElement) || !this.contains(action)) return;

      const row = action.closest('[data-cart-line][data-key][data-quantity]');
      if (!(row instanceof HTMLElement)) return;

      event.preventDefault();

      const current = this.#number(row.dataset.quantity, 1);

      // Remove must depend only on Shopify's line key. Multipack rows intentionally use
      // a separate pack-quantity input, so looking for the standard quantity input first
      // would abort Remove before the cart mutation is sent.
      if (action.hasAttribute('data-zyma-cart-remove')) {
        this.#changeItem(row, 0);
        return;
      }

      const input = row.querySelector('[data-zyma-cart-quantity-input]');
      if (!(input instanceof HTMLInputElement)) return;

      const min = this.#number(input.min, 1);
      const step = Math.max(1, this.#number(input.step, 1));
      const max = input.max ? this.#number(input.max, Infinity) : Infinity;
      let quantity = current;

      if (action.hasAttribute('data-zyma-cart-increase')) quantity = Math.min(max, current + step);
      if (action.hasAttribute('data-zyma-cart-decrease')) quantity = Math.max(min, current - step);

      if (quantity !== current) this.#changeItem(row, quantity);
    };

    #onChange = (event) => {
      if (!(event.target instanceof HTMLInputElement) || !event.target.matches('[data-zyma-cart-quantity-input]')) return;
      if (this.#isUpdating) return;

      const row = event.target.closest('[data-cart-line][data-key][data-quantity]');
      if (!(row instanceof HTMLElement)) return;

      const current = this.#number(row.dataset.quantity, 1);
      const quantity = this.#normalizeQuantity(event.target, current);

      if (quantity === current) {
        event.target.value = String(current);
        return;
      }

      this.#changeItem(row, quantity);
    };

    #onKeyDown = (event) => {
      if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
      if (!event.target.matches('[data-zyma-cart-quantity-input]')) return;
      event.preventDefault();
      event.target.blur();
    };

    #onExternalCartUpdate = async (event) => {
      const source = event.detail?.data?.source;
      const sourceId = event.detail?.sourceId;
      if (source === 'cart-items-component' && sourceId === this.sectionId) return;

      if (this.#isUpdating) {
        this.#needsRefresh = true;
        return;
      }

      const sections = event.detail?.data?.sections || event.detail?.resource?.sections;
      const sectionHTML = sections?.[this.sectionId];

      try {
        if (sectionHTML) this.#replaceFromSectionHTML(sectionHTML);
        else await this.#refreshSection();
      } catch (error) {
        console.error('[Zyma cart] external refresh failed', error);
      }
    };

    async #changeItem(row, quantity) {
      if (this.#isUpdating) return;

      const key = row.dataset.key;
      if (!key) return;

      const current = this.#number(row.dataset.quantity, 1);
      if (quantity === current) return;

      this.#isUpdating = true;
      this.#clearErrors();
      this.#setBusy(true);

      try {
        const sections = this.#sectionIds();
        const response = await fetch(this.#changeUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json().catch(() => null);
        if (!response.ok || !data || data.errors) {
          throw new Error(this.#errorMessage(data, response.status));
        }

        const sectionHTML = data.sections?.[this.sectionId];
        if (!sectionHTML) throw new Error('Shopify did not return the cart section.');

        this.#replaceFromSectionHTML(sectionHTML);

        this.dispatchEvent(
          new CustomEvent(CART_UPDATE_EVENT, {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: this.sectionId,
              data: {
                source: 'cart-items-component',
                itemCount: Number(data.item_count) || 0,
                sections: data.sections || {},
              },
            },
          })
        );
      } catch (error) {
        console.error('[Zyma cart] mutation failed', error);
        try {
          await this.#refreshSection();
        } catch (refreshError) {
          console.error('[Zyma cart] recovery refresh failed', refreshError);
        }
        this.#showError(key, this.#localizedError(error instanceof Error ? error.message : ''));
      } finally {
        this.#isUpdating = false;
        this.#setBusy(false);

        if (this.#needsRefresh) {
          this.#needsRefresh = false;
          try {
            await this.#refreshSection();
          } catch (error) {
            console.error('[Zyma cart] queued refresh failed', error);
          }
        }
      }
    }

    #replaceFromSectionHTML(sectionHTML) {
      const documentFragment = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const components = [...documentFragment.querySelectorAll('cart-items-component')];
      let source = components.find((component) => component.dataset.sectionId === this.sectionId) || components[0];

      // The cart page's empty state historically renders without a cart-items wrapper.
      // Keep this live component mounted and use the server-rendered empty-state content.
      if (!source) {
        const pageInner = documentFragment.querySelector('[data-cart-inner]');
        if (pageInner && !pageInner.querySelector('cart-items-component')) source = pageInner;
      }

      if (!source) throw new Error(`Cart component ${this.sectionId} missing from Shopify section response.`);

      const fragment = document.createDocumentFragment();
      [...source.childNodes].forEach((node) => fragment.appendChild(node.cloneNode(true)));
      this.replaceChildren(fragment);
    }

    async #refreshSection() {
      const url = new URL(window.location.href);
      url.searchParams.set('section_id', this.sectionId);

      const response = await fetch(url.toString(), {
        method: 'GET',
        credentials: 'same-origin',
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
      });

      if (!response.ok) throw new Error(`Cart refresh failed with HTTP ${response.status}`);
      this.#replaceFromSectionHTML(await response.text());
    }

    #sectionIds() {
      const ids = new Set([this.sectionId]);
      for (const component of document.querySelectorAll('cart-items-component[data-section-id]')) {
        if (!(component instanceof HTMLElement) || !component.dataset.sectionId) continue;
        ids.add(component.dataset.sectionId);
        if (ids.size >= MAX_SECTIONS) break;
      }
      return [...ids];
    }

    #changeUrl() {
      const root = window.Shopify?.routes?.root || '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      return `${normalizedRoot}cart/change.js`;
    }

    #normalizeQuantity(input, fallback) {
      const min = this.#number(input.min, 1);
      const step = Math.max(1, this.#number(input.step, 1));
      const max = input.max ? this.#number(input.max, Infinity) : Infinity;
      const raw = Number.parseInt(input.value, 10);

      if (!Number.isFinite(raw)) return fallback;

      let quantity = Math.max(min, raw);
      quantity = min + Math.round((quantity - min) / step) * step;
      quantity = Math.max(min, Math.min(max, quantity));
      return quantity;
    }

    #setBusy(isBusy) {
      if (isBusy) this.setAttribute('aria-busy', 'true');
      else this.removeAttribute('aria-busy');

      if ('inert' in this) this.inert = isBusy;
    }

    #clearErrors() {
      this.querySelectorAll('[data-zyma-cart-error]').forEach((element) => {
        element.textContent = '';
        element.classList.add('hidden');
      });
    }

    #showError(key, message) {
      const row = [...this.querySelectorAll('[data-cart-line][data-key]')].find((item) => item.dataset.key === key);
      const error = row?.querySelector('[data-zyma-cart-error]') || this.querySelector('[data-zyma-cart-error]');
      if (!(error instanceof HTMLElement)) return;
      error.textContent = message;
      error.classList.remove('hidden');
    }

    #localizedError(message) {
      const value = String(message || '').trim();
      const isArabic = document.documentElement.lang.toLowerCase().startsWith('ar');
      if (!isArabic) return value || 'We couldn’t update your bag. Please try again.';
      if (/[\u0600-\u06FF]/.test(value)) return value;
      return 'مقدرناش نحدّث السلة. جرّب تاني.';
    }

    #errorMessage(data, status) {
      if (data?.description) return String(data.description);
      if (data?.message) return String(data.message);
      if (typeof data?.errors === 'string') return data.errors;
      if (data?.errors && typeof data.errors === 'object') return Object.values(data.errors).flat().join(' ');
      return status ? `We couldn’t update your bag (HTTP ${status}). Please try again.` : 'We couldn’t update your bag. Please try again.';
    }

    #number(value, fallback) {
      const number = Number(value);
      return Number.isFinite(number) ? number : fallback;
    }
  }

  if (!customElements.get('cart-items-component')) {
    customElements.define('cart-items-component', CartItemsComponent);
  }
})();
